import { ChangeEvent } from 'react';

import { ProductAnalyticsBpContext } from '@vimeo/use-tracking';

import { CONTEXT_RESOURCE_TYPE } from '../../api/constants';
import T from '../../translate';
import { useGlobalStore } from '../state/globalStore';
import {
  CustomFieldTypes,
  ENTITY_TO_PATH_MAP,
  ENTITY_TYPE,
  FORM_FIELD_NAME_VALUES,
  LC_FIELDS,
  LC_URL_QUERY_PARAMS,
  PREVIEW_HTML_KEYS,
  RESPONSE_KEYS_MAP,
  URL_QUERY_PARAMS,
} from './constants';
import { BackgroundType, Enum, Field, LogoType } from './types';

export const LEAD_CAPTURE_COOKIE_NAME_PREFIX = 'lc_';

/**
 * @param str string to hash
 * @param seed changing seed will change output for same string
 * @returns a hashed value - 1377088794910075
 */
const generateHash = (str: string, seed = 0) => {
  let h1 = 0xdeadbeef ^ seed,
    h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString();
};

export const getCookie = (name?: string) => {
  if (!name || typeof document === 'undefined') return false;

  /** From Mid July 2024, we changed the cookie name syntax */
  const cookieNameV2 = LEAD_CAPTURE_COOKIE_NAME_PREFIX + getEntityIdFromApiEntityId(name);
  const pattern = RegExp(cookieNameV2 + '=.[^;]*');
  const matched = document.cookie.match(pattern);
  if (matched) {
    const cookie = matched[0].split('=');
    return cookie[1];
  } else {
    /** If no cookie matched, check for cookie with old cookie name (from before Mid July 2024) */
    /**
     * TODO: We can do cleanup of this v1 cookie code after 2 weeks of deployment
     */
    const cookieNameV1 = LEAD_CAPTURE_COOKIE_NAME_PREFIX + generateHash(name);
    const pattern = RegExp(cookieNameV1 + '=.[^;]*');
    const matched = document.cookie.match(pattern);
    if (matched) {
      const cookie = matched[0].split('=');
      return cookie[1];
    }
  }
  return false;
};

export const setCookie = (name: string, value: string, daysToExpire = 7) => {
  const date = new Date();
  date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
  const expires = 'expires=' + date.toUTCString();
  // Allow subdomains to access cookie by removing the prefixes for the host.
  const domain = 'domain=' + window.location.hostname?.split('.').splice(-2).join('.');
  const cookieName = LEAD_CAPTURE_COOKIE_NAME_PREFIX + getEntityIdFromApiEntityId(name);
  document.cookie =
    cookieName + '=' + value + '; ' + expires + ';' + domain + '; path=/; SameSite=None; Secure';
};

export const deleteCookie = (name: string) => {
  /** We change cookie name syntax (around mid July 2024), hence attempt deleting both cookies */
  /**
   * TODO: We can do cleanup of this v1 cookie code after 2 weeks of deployment
   */
  const cookieNameV2 = LEAD_CAPTURE_COOKIE_NAME_PREFIX + getEntityIdFromApiEntityId(name);
  const cookieNameV1 = LEAD_CAPTURE_COOKIE_NAME_PREFIX + generateHash(name);
  document.cookie = cookieNameV2 + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  document.cookie = cookieNameV1 + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;';
};

export const getEntityIdFromApiEntityId = (apiEntityId: string) => {
  return apiEntityId.split(':')[0];
};

export const snakeToCamelCase = (item: unknown): any => {
  if (Array.isArray(item)) {
    return item.map((el: unknown) => snakeToCamelCase(el));
  } else if (typeof item === 'function' || item !== Object(item)) {
    return item;
  }
  return Object.fromEntries(
    Object.entries(item as Record<string, unknown>).map(([key, value]: [string, unknown]) => [
      key.replace(/([-_][a-z])/gi, (c) => c.toUpperCase().replace(/[-_]/g, '')),
      snakeToCamelCase(value),
    ]),
  );
};

export const responseTransformer = (
  key: string,
  value: any,
  options?: { resolveTags?: boolean },
) => {
  const htmlPreviewKeys = Object.keys(PREVIEW_HTML_KEYS);
  // The backend would return tags resolved and unresolved html
  if (htmlPreviewKeys.includes(key)) {
    if (options?.resolveTags) {
      return value['tagsResolved'];
    } else {
      return value['tagsUnresolved'];
    }
  }

  // Populate the value of logo.url
  if (key === RESPONSE_KEYS_MAP.logo) {
    const logo: LogoType = value;
    if (!logo.uri) {
      return { ...logo, pictures: undefined, url: undefined, isActive: false };
    }
    const highResUrl =
      logo.pictures?.sizes?.reduce((acc, pic) => (pic.width > acc.width ? pic : acc), {
        width: 0,
        link: '',
      }).link || '';
    return { ...logo, pictures: undefined, url: removeMhAndMwFromUrl(highResUrl) };
  }

  // Populate the value of background.url
  if (key === RESPONSE_KEYS_MAP.background) {
    const background: BackgroundType = value;
    let highResUrl;
    highResUrl =
      background.pictures?.sizes?.reduce((acc, pic) => (pic.width > acc.width ? pic : acc), {
        width: 0,
        link: '',
      }).link || '';
    return { ...background, pictures: undefined, url: removeMhAndMwFromUrl(highResUrl) };
  }
  return value;
};

// The backend is adding mw and mh which would result in low quality image for some cases
const removeMhAndMwFromUrl = (imageUrl: string) => {
  // So we fix that here.
  try {
    const url = new URL(imageUrl);
    const searchParams = new URLSearchParams(url.search);
    searchParams.delete('mw');
    searchParams.delete('mh');
    url.search = searchParams.toString();
    return url.toString();
  } catch (e) {
    return imageUrl;
  }
};

const EMAIL_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const isValidEmail = (email: string) => {
  return EMAIL_REGEX.test(email);
};

export const getValidator = <T extends string | boolean>(
  fieldName: string,
  required?: boolean,
): ((value: T) => string | undefined) | undefined => {
  switch (fieldName) {
    case FORM_FIELD_NAME_VALUES.EMAIL:
      return ((value: string) => (isValidEmail(value) ? undefined : T.InvalidEmailAddress)) as (
        value: T,
      ) => string | undefined;
    default:
      return required
        ? (value: T) => {
            if (typeof value === 'string') {
              return value.length > 0 ? undefined : T.ThisFieldIsRequired;
            }
            if (typeof value === 'boolean') {
              return value ? undefined : T.ThisFieldIsRequired;
            }
            return undefined;
          }
        : () => undefined;
  }
};

export const formatFields = (fields: Field[]): Field[] => {
  const allowedTypes = [CustomFieldTypes.Checkbox, CustomFieldTypes.Dropdown].map((type) =>
    type.toString(),
  );
  const highestId = fields.length > 0 ? Math.max(...fields.map((f) => f.id || 0)) : 1;

  return fields
    .map((field, i) => {
      const metadata = allowedTypes.includes(field.type)
        ? {
            description: field.metadata?.description,
            color: field.metadata?.color,
            options: field.metadata?.options
              ? field.metadata.options
                  .map((option) => ({ ...option, cid: option.optionPosition }))
                  .sort((a, b) => a.optionPosition - b.optionPosition)
              : undefined,
          }
        : field.metadata;

      return {
        ...field,
        cid: field.id ?? highestId + 1 + i,
        metadata,
      };
    })
    .sort((a, b) => a.position - b.position);
};

/**
 * Checks if two dates are the same.
 * @param {Date} date1 - The first date to compare.
 * @param {Date} date2 - The second date to compare.
 * @returns {boolean} Returns true if the dates are the same, false otherwise.
 */
export const isSameDate = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const getTimeZone = (locale: string, date: Date = new Date()) =>
  date.toLocaleDateString(locale, { day: '2-digit', timeZoneName: 'short' }).substring(4);

/**
 * Return owner id of event from uri of owner.
 * @param uri /users/190194509
 * @return 19019509
 */
export const getEntityOwnerIdFromUserUri = (uri: string) => {
  const splitURI = uri.split('/');
  if (splitURI.length !== 3) {
    throw 'There is an issue with the uri of the user';
  }
  return parseInt(splitURI[2]);
};

const getCookiePassword = (cookieName: string) => {
  const password = getCookieByName(cookieName);
  return password.length > 0 ? password : undefined;
};

export const getPasswordFromQueryParam = () => {
  const fallBackPassword =
    getValueFromQueryParam(URL_QUERY_PARAMS.PASSWORD) ||
    getValueFromQueryParam(LC_URL_QUERY_PARAMS.PASSWORD) ||
    '';
  return window.encodeURIComponent(fallBackPassword);
};

export const getEntityCookiePassword = (entityId: string, entityType: Enum<typeof ENTITY_TYPE>) => {
  /**
   * Event:
   * The password will be present in url param, its set in frontend/apps/embed/libs/embed-playlist/containers/ErrorContainer.jsx
   *
   * Video:
   * The password will be set by the player from player codebase in the url param
   */
  const fallBackPassword = getPasswordFromQueryParam();
  switch (entityType) {
    case ENTITY_TYPE.EVENT:
      return getCookiePassword(`${entityId}_rlepassword`) || fallBackPassword;
    case ENTITY_TYPE.VIDEO:
      return getCookiePassword(`${entityId}_password`) || fallBackPassword;
    case ENTITY_TYPE.SHOWCASE:
      return getCookiePassword(`${entityId}_albumpassword`) || fallBackPassword;
  }
};

/**
 * Read a cookie by its name
 */
const getCookieByName = (cname: string) => {
  let name = cname + '=';
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
};

export const getIdFromLeadUri = (uri: string): string => {
  const splitted = uri.split('/');
  return splitted.pop() || '';
};

export const getEventAppearanceURI = (eventId: string): string => {
  return `/manage/events/${eventId}/appearance`;
};

/**
 * Used to check whether the onChangeEvent is triggered by autofill or not
 * @returns true if triggered by autofill
 */
export const hasAutoFilled = (e: ChangeEvent<HTMLInputElement>) => {
  try {
    const inputType = (e.nativeEvent as InputEvent)?.['inputType'];
    /** absence of inputType means its auto-filled */
    return !inputType;
  } catch (err) {
    return true;
  }
};

/**
 * @return {String} url
 */
export const getSourceUrl = (): string | undefined => {
  // Check if in an iframe and parent has the same origin
  if (isIFramed() && isWindowParentSameOrigin() && window.top) {
    return hasQuery(window.top.location.href) ? window.top.location.href : window.location.href;
  }

  // Check if location is available
  if (window.location.href) {
    return window.location.href;
  }

  // Fallback to document referrer (less reliable)
  return document.referrer;
};

export const setQueryParams = (url: string, params: Record<string, string>) => {
  const builder = new URL(url);
  for (const [key, value] of Object.entries(params)) {
    builder.searchParams.set(key, value);
  }
  return builder.toString();
};

/**
 * @return {Boolean} iframe
 */
const isIFramed = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    // assume we're in an iframe if accessing either of those properties throws an exception
    return true;
  }
};

export const getValueFromQueryParam = (
  param: Enum<typeof URL_QUERY_PARAMS | typeof LC_URL_QUERY_PARAMS>,
) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
};

/**
 * @return {Boolean} sameOrigin
 */
export const isWindowParentSameOrigin = () => {
  try {
    // Get the parent window's origin
    const parentOrigin = window.top?.origin;

    // Get the iframe's origin
    const iframeOrigin = window.origin;

    // Compare the two origins to check if they're the same
    return parentOrigin === iframeOrigin;
  } catch {
    // assume we're in an iframe if accessing throws an exception
    return false;
  }
};

/**
 * @param {string} url
 * @return {Boolean}
 */
const hasQuery = (url: string) => {
  return url.includes('?');
};

// Used to generate random string ID
export const makeId = (length: number) => {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
};

export const getFormattedEntityType = (
  entityType: Enum<typeof ENTITY_TYPE> | null,
): ProductAnalyticsBpContext['entity_type'] => {
  switch (entityType) {
    case ENTITY_TYPE.EVENT:
      //TODO: event has events in BP schema while other don't have, need to fix this
      return 'events';
    case ENTITY_TYPE.VIDEO:
      return 'video';
    case ENTITY_TYPE.SHOWCASE:
      return 'showcase';
    default:
      return null;
  }
};

export const getLeadCaptureUrl = (
  entityPassword: string | null,
  leadId: string | false,
  select?: string[],
  parentLeadUuid?: string,
) => {
  const entityId = useGlobalStore.getState().apiEntityId;
  const entityType = useGlobalStore.getState().entityType || ENTITY_TYPE.EVENT;
  const fields = select ? select.join(',') : LC_FIELDS.join(',');
  let url = `/lead_capture/${ENTITY_TO_PATH_MAP[entityType]}/${entityId}/form?fields=${fields}`;
  if (leadId) {
    url += `&lead_id=${leadId}`;
  }
  if (entityPassword) {
    url += `&password=${entityPassword}`;
  }
  if (parentLeadUuid) {
    url += `&parent_lead_uuid=${parentLeadUuid}`;
  }
  url += `&is_embed=true`;
  return url;
};

const getIframeUrl = (): string => {
  let referrer = document.referrer;
  try {
    /** originalUrl is set at frontend/apps/embed/libs/embed-playlist/containers/EmbedPlaylistApp.jsx */
    referrer =
      (window as unknown as { parent?: { originalUrl?: string } }).parent?.originalUrl ||
      document.referrer;
  } catch (e) {
    console.error(e);
  }
  return referrer;
};

/**
 * Why?
 * In case of CG inside a video inside a showcase, the CG does not have access to the video password to call the API.
 * So what we are doing is to use album password and check whether the user has access to album and whether the video is inside the album.
 * By doing so we can bypass the password for the video.
 *
 * How?
 * The checks for these are done in the backend, so we need to pass the album id and album password to the backend for all the requests.
 */
export const getAlbumDetailsForVideo = () => {
  const entityType = useGlobalStore.getState().entityType || ENTITY_TYPE.EVENT;
  if (entityType === ENTITY_TYPE.VIDEO) {
    let referrer = getIframeUrl();
    const SHOWCASE_REGEX = /\/showcase\/(\d+)/;
    const match = referrer.match(SHOWCASE_REGEX);
    const albumId = match ? match[1] : null;
    if (albumId) {
      const albumPassword = getCookiePassword(`${albumId}_albumpassword`);
      return {
        resourceId: albumId,
        resourcePassword: albumPassword,
        resourceType: CONTEXT_RESOURCE_TYPE.ALBUM,
      };
    }
  }
  return null;
};

/**
 * Why?
 * In case of CG inside a video inside an event, the CG does not have access to the event password to call the API.
 * So what we are doing is to use event password and check whether the user has access to event and whether the video is inside the event.
 * By doing so we can bypass the password for the video.
 *
 * How?
 * The checks for these are done in the backend, so we need to pass the event id and event password to the backend for all the requests.
 */
export const getEventDetailsForVideo = () => {
  const entityType = useGlobalStore.getState().entityType || ENTITY_TYPE.EVENT;
  if (entityType === ENTITY_TYPE.VIDEO) {
    const EVENT_REGEX = /\/event\/(\d+)(\/embed(\/interaction)?)?/;
    let referrer = getIframeUrl();
    const eventMatch = referrer.match(EVENT_REGEX);
    if (eventMatch) {
      const eventId = eventMatch ? eventMatch[1] : null;
      if (eventId) {
        const eventPassword = getCookiePassword(`${eventId}_rlepassword`);
        return {
          resourceId: eventId,
          resourcePassword: eventPassword,
          resourceType: CONTEXT_RESOURCE_TYPE.EVENT,
        };
      }
    }
  }
  return null;
};

export const getContextDetailsForCG = () => {
  return getAlbumDetailsForVideo() || getEventDetailsForVideo();
};
